import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getDeclarationAuditLogs,
  getDeclarationData,
  getMatchedDeclarationsStatusOptions,
  getUnmatchedDeclarationsLinkOptions,
  getUnmatchedDeclarationsStatusOptions,
  linkDeclaration,
  setMatchedDeclarationStatus,
  setUnmatchedDeclarationStatus,
  unlinkDeclaration,
} from '../../../api/declarations';
import { formatUkDateFromString } from '../../../utils/stringUtils';
import { Input, Select } from '../../Input/Input';
import SimpleTable from '../../SimpleTable';
import Toast from '../../Toast/Toast';
import './DeclarationsActions.css';

const DeclarationsActions = () => {
  let { id, loadId, portalId } = useParams();
  const navigate = useNavigate();

  const [linkReference, setLinkReference] = useState('');
  const [linkDropdownValue, setLinkDropdownValue] = useState(null);
  const [connectTicketReference, setConnectTicketReference] = useState('');
  const [status, setStatus] = useState(null);
  const [declarationDetails, setDeclarationDetails] = useState();

  const [loadingData, setLoadingData] = useState(true);
  const [loadingAuditLog, setLoadingAuditLog] = useState(true);
  const [updatingData, setUpdatingData] = useState(false);

  const [auditLog, setAuditLog] = useState({ totalCount: 0, data: [] });

  const [attachDetachOptions, setAttachDetachOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const [isMatched, setIsMatched] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const getAuditLog = useCallback(async () => {
    setLoadingAuditLog(true);
    const log = await getDeclarationAuditLogs(id);
    setAuditLog(log);
    setLoadingAuditLog(false);
  }, [id]);

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      const linkOptions = await getUnmatchedDeclarationsLinkOptions();
      setAttachDetachOptions(linkOptions);

      const statusOpts = isMatched
        ? await getMatchedDeclarationsStatusOptions()
        : await getUnmatchedDeclarationsStatusOptions();
      setStatusOptions(statusOpts);
    };

    fetchDropdownOptions();
  }, [isMatched]);

  useEffect(() => {
    const fetchDeclarationData = async () => {
      setLoadingData(true);

      const [declarationDetails] = await getDeclarationData(id, portalId && loadId);
      setDeclarationDetails(declarationDetails);
      setIsMatched(portalId && loadId);

      let statusValue = null;
      if (declarationDetails?.declarationType === 'SFD') {
        statusValue = 10;
      }
      if (declarationDetails?.declarationType === 'SDI') {
        statusValue = 20;
      }

      setLinkDropdownValue(statusValue);
      setLinkReference(declarationDetails?.reference || '');
      setStatus(declarationDetails ? declarationDetails.declarationStatus : null);
      setConnectTicketReference(declarationDetails.connectTicketReference);
      setLoadingData(false);
    };

    getAuditLog();
    fetchDeclarationData();
  }, [getAuditLog, id, portalId, loadId]);

  const overviewTableData = {
    headings: [
      { label: 'Declarations Date', key: 'declarationsDate' },
      { label: 'LRN', key: 'LRN' },
      { label: 'MRN', key: 'MRN' },
    ],
    values: [declarationDetails],
  };

  const historyTableData = {
    headings: [
      { label: 'Date / Time', key: 'lastModified' },
      { label: 'Who?', key: 'user' },
      { label: 'Action', key: 'action' },
    ],
    values: auditLog.data.map((log) => ({ ...log, lastModified: formatUkDateFromString(log.lastModified, true) })),
  };

  const handleLinkOrUnlink = async () => {
    setUpdatingData(true);

    const resp = isMatched ? await unlinkDeclaration(id) : await linkDeclaration(id, linkReference, linkDropdownValue);
    const data = resp?.data?.[0] || resp?.[0];

    if (!data) {
      displayError('An unknown error occurred. Please try again later.');
      setUpdatingData(false);
      return;
    }

    if (data.error || data.ErrorMessage) {
      displayError(data.error || data.ErrorMessage);
      setUpdatingData(false);
      return;
    }

    if (data.response === 'Success') {
      navigate(isMatched ? -1 : '/UnmatchedDeclarations');
    }
  };

  const handleSetStatus = async () => {
    setUpdatingData(true);

    const resp = isMatched
      ? await setMatchedDeclarationStatus(id, status, connectTicketReference)
      : await setUnmatchedDeclarationStatus(id, status, connectTicketReference);
    const data = resp?.data?.[0] || resp?.[0];

    if (data.status === 'inserted') {
      await getAuditLog();
      const updatedDetails = { ...declarationDetails, declarationStatus: status };
      setDeclarationDetails(updatedDetails);
    }

    setUpdatingData(false);
  };

  const getLinkButtonText = () => {
    if (updatingData) {
      return <Spinner animation="border" size="sm" />;
    }

    return isMatched ? <>Detach Link</> : <>Link</>;
  };

  const displayError = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const validateStatusReference = () => {
    const statusRegex = /^EC-[0-9]{4}$/;
    return !connectTicketReference || statusRegex.test(connectTicketReference);
  };

  return (
    <div className="full-width-container declarations-actions">
      <Toast className="error-toast">
        <Alert style={{ maxWidth: '35rem', backgroundColor: '#E4E3ED' }} show={showToast}>
          <button
            className="btn-close position-absolute top-0 end-0 p-3"
            onClick={() => {
              setShowToast(false);
            }}
          />
          <div className="floating-box-header-newload align-items-center" style={{ margin: '0px 30px', padding: 0 }}>
            <span>An error occurred</span>
          </div>
          <p style={{ fontSize: '100%', fontWeight: 500 }} className="text-danger">
            {toastMessage}
          </p>
        </Alert>
      </Toast>

      <section className="floating-box position-relative p-3" style={{ maxWidth: '1300px' }}>
        <h1 className="pb-2">{!isMatched && 'Unmatched '}Declaration Actions</h1>
        {loadingData && <Spinner animation="border" />}
        {!loadingData && !declarationDetails && <>No data found</>}
        {!loadingData && declarationDetails && (
          <>
            <div className="pb-4">
              <SimpleTable data={overviewTableData} />
            </div>

            <h2 className="m-0">Actions</h2>
            <div className="d-flex">
              <div className="d-flex justify-content-between input-container">
                <Input
                  name="linkReference"
                  type="text"
                  value={linkReference}
                  onChange={(e) => setLinkReference(e.target.value)}
                  disabled={isMatched || declarationDetails?.disableActions}
                />
                <Select
                  name="LinkDropdown"
                  className="ps-3"
                  data={linkDropdownValue}
                  setData={setLinkDropdownValue}
                  options={attachDetachOptions}
                  disabled={isMatched || declarationDetails?.disableActions}
                />
              </div>
              <Button
                className="align-self-end input-inner-container ms-3 actions-buttons"
                onClick={handleLinkOrUnlink}
                disabled={updatingData || !linkReference || !linkDropdownValue || declarationDetails?.disableActions}
              >
                {getLinkButtonText()}
              </Button>
            </div>

            <div className="d-flex pb-4">
              <div className="d-flex justify-content-between input-container">
                <Input
                  name="linkReference"
                  type="text"
                  value={connectTicketReference}
                  onChange={(e) => setConnectTicketReference(e.target.value.toUpperCase())}
                  error={!validateStatusReference() && 'Connect ticket is not in the correct format e.g EC-1234'}
                />
                <Select
                  name="status"
                  className="w-100 ps-3"
                  data={status}
                  setData={setStatus}
                  options={statusOptions}
                />
              </div>

              <Button
                className="align-self-end input-inner-container ms-3 actions-buttons"
                onClick={handleSetStatus}
                disabled={
                  !status ||
                  (status === declarationDetails?.declarationStatus &&
                    connectTicketReference === declarationDetails?.connectTicketReference) ||
                  updatingData ||
                  !validateStatusReference()
                }
              >
                {updatingData ? <Spinner animation="border" size="sm" /> : 'Set Status'}
              </Button>
            </div>

            <h2 className="pb-2">History</h2>
            <SimpleTable data={historyTableData} />

            {loadingAuditLog && <Spinner animation="border" />}

            <Button
              className="mt-4 align-self-end input-inner-container"
              onClick={() => navigate(-1)}
              disabled={updatingData}
            >
              Back
            </Button>
          </>
        )}
      </section>
    </div>
  );
};

export default DeclarationsActions;
